// Page layouts on the member side (design 5.4, AdminLayout.dc.html's phase 1 settings; decision 25):
// sections shown, hidden and moved per the published layout, with each type's default in code, and
// the same order on a phone. Pure, so a test can import it.
//
// - The published layout comes from hub.my_layout(product, page): published only, never a draft;
//   0 rows means the default below.
// - A layout holds an order of section keys, each shown or hidden. Sections marked fixed hold the
//   course itself, so they move but never hide (the database refuses to publish one hidden; the
//   renderer shows a fixed section whatever it's told).
// - A key the code doesn't know yet (a phase 2 section) is left out; a section the code has but the
//   published layout lacks goes in after the section before it in the default.
// - On a laptop, sections that sit side by side (FEWeek's main column and side column, FEHome's
//   next session beside carry on watching) are drawn in bands: a run of sections between 2 full
//   width ones, its main ones on the left and its side ones on the right, each column in the
//   layout's order. On a phone, and in the widths between, every section is 1 column, in the
//   layout's order (BUILD-PLAN's "a moved section keeps its place at phone width").

const sec = (key, extra) => ({ key, shown: true, fixed: false, ...extra })

// AdminLayout.dc.html's defaults, the same as the seeded version 1 of The Funding Engine's pages
export const LIVE_COHORT_HOME = [
  sec('news'), sec('next'), sec('resume'), sec('weeks', { fixed: true }), sec('todo'), sec('help'), sec('res'),
]
export const WEEK_PAGE = [
  sec('wnews'), sec('video', { fixed: true }), sec('summary'), sec('five'), sec('learn'), sec('hw'), sec('sheet'), sec('prompts'),
]

// The mastermind's home (Accelerator.dc.html, Returning member, top to bottom): Adam's announcement,
// the goal beside the next session (with attendance and whether they're on track), this month's actions beside
// the group's WhatsApp and the wins, the latest recording, then a shortcut to each Resources tab (shorter since the
// Accelerator critique's member 5, 20 Oct). This month's actions hold the mastermind itself, so they move but never
// hide. The welcome checklist and the payment banner sit above every home, outside the layout (ProductRoute.jsx).
// On a phone the home keeps its own lead (src/products/accelerator/bands.js stackedKeys).
export const MASTERMIND_HOME = [
  sec('news'), sec('goal'), sec('next'), sec('actions', { fixed: true }), sec('group'), sec('wins'), sec('recordings'), sec('res'),
]

// Profit On Your Terms's home (POYT.dc.html, Paid in full, under its hero, which isn't a section):
// Adam's announcement, Start here beside the tool card and the questions card, the 6 weeks,
// Resources, then the scripts guarantee beside the deal surgery (which shows only for an intake
// with it on). Start here and the 6 weeks hold the course itself, so they move but never hide. Its
// week page is the shared recording page's, as The Funding Engine's.
export const POYT_HOME = [
  sec('news'), sec('start', { fixed: true }), sec('tool'), sec('help'), sec('weeks', { fixed: true }), sec('res'), sec('guarantee'), sec('surgery'),
]

// Each type's default layout per page, where it has one (the masterclass has none). Profit On Your
// Terms is a live cohort with a home of its own, so it has its own entry (layoutKind).
export const DEFAULT_LAYOUTS = {
  live_cohort: { home: LIVE_COHORT_HOME, week: WEEK_PAGE },
  on_demand: { week: WEEK_PAGE },
  mastermind: { home: MASTERMIND_HOME },
  poyt: { home: POYT_HOME, week: WEEK_PAGE },
}

// What the admin's Page layout calls each section and says it is (AdminLayout.dc.html's list, for
// the 2 homes no board draws there), for the admin screens' sectionWords. news is the same as
// every other home's ("Announcements").
export const HOME_SECTION_WORDS = {
  mastermind: {
    goal: ['Your goal', 'The goal, when it was locked, and the month of 12'],
    next: ['Next session', "The date, Join, Add to calendar, attendance and whether they're on track"],
    actions: ["This month's actions", 'The actions set at the last session, ticked as they go, with a note for you'],
    group: ['Your group', "The group's WhatsApp link"],
    wins: ['Your wins', 'Their latest 3 wins, the rest a tap away, and Add a win'],
    recordings: ['Latest recording', "Their group's latest session, with Watch and All recordings"],
    res: ['Resources', 'A shortcut to each tab, with how many are in it'],
  },
  poyt: {
    start: ['Start here', "Deal analysis and marketing setup, with where they're up to"],
    tool: ['Tool', 'The two-offer analyser, opened in the hub'],
    help: ['Questions between sessions', 'How to reach Adam between sessions'],
    weeks: ['Your 6 weeks', "Every week, its date, its pack and what they've done"],
    res: ['Resources', 'Before Week 1, the marketing toolkit, weekly packs, tools and bonuses, in tabs'],
    guarantee: ['The scripts guarantee', 'What it covers and where to send a situation'],
    surgery: ['Your deal surgery', 'Shows only for an intake with the deal surgery on'],
  },
}

// Which defaults a product's pages take: Profit On Your Terms's own (by slug, as its screens are:
// screens.js), else its type's
export function layoutKind(product) {
  if (product?.slug === 'poyt') return 'poyt'
  return product?.type || null
}

export function defaultLayout(type, page) {
  return DEFAULT_LAYOUTS[type]?.[page] || null
}

// The default for 1 product's page: defaultLayoutFor({ slug: 'poyt', type: 'live_cohort' }, 'home')
export function defaultLayoutFor(product, page) {
  return defaultLayout(layoutKind(product), page)
}

// The order to draw: the published sections where there are some, checked against the default
export function resolveLayout(published, defaults) {
  if (!defaults) return []
  const known = new Map(defaults.map(d => [d.key, d]))
  if (!Array.isArray(published) || !published.length) return defaults.map(d => ({ ...d }))
  const out = []
  for (const p of published) {
    if (!p || typeof p.key !== 'string' || !known.has(p.key) || out.some(o => o.key === p.key)) continue
    const fixed = known.get(p.key).fixed
    out.push({ key: p.key, shown: fixed ? true : p.shown !== false, fixed })
  }
  // a section the published layout doesn't have yet goes after its neighbour in the default
  defaults.forEach((d, i) => {
    if (out.some(o => o.key === d.key)) return
    let at = 0
    for (let j = i - 1; j >= 0; j--) {
      const k = out.findIndex(o => o.key === defaults[j].key)
      if (k >= 0) { at = k + 1; break }
    }
    out.splice(at, 0, { ...d })
  })
  return out
}

// The sections to draw, in order: shown, known to the page, and not empty
export function shownKeys(sections, parts) {
  return (sections || []).filter(s => s.shown && parts[s.key] && parts[s.key].when !== false).map(s => s.key)
}

// Laptop bands: [{ full: key } | { main: [keys], side: [keys], columns }]
export function layoutBands(sections, parts) {
  const out = []
  let band = null
  for (const key of shownKeys(sections, parts)) {
    const place = parts[key].place || 'full'
    if (place === 'full') {
      band = null
      out.push({ full: key })
      continue
    }
    if (!band) {
      band = { main: [], side: [], columns: null }
      out.push(band)
    }
    band[place === 'side' ? 'side' : 'main'].push(key)
    if (!band.columns && parts[key].columns) band.columns = parts[key].columns
  }
  return out
}
